import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import { Bike, Plus, Trash2, Edit2, Phone, User, Key, Save } from 'lucide-react';

export default function AdminRiders() {
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {id?, name, phone, pin, active}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await adminApi.get('/admin/riders'); setRiders(r.data || []); }
    catch (e) { toast.error('Errore caricamento rider'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => setForm({ name: '', phone: '', pin: '', vehicle: '', working_hours: '', zone: '', active: true });
  const startEdit = (r) => setForm({ id: r.id, name: r.name, phone: r.phone, pin: '', vehicle: r.vehicle || '', working_hours: r.working_hours || '', zone: r.zone || '', active: r.active });

  const save = async () => {
    if (!form.name || !form.phone) { toast.error('Nome e cellulare obbligatori'); return; }
    if (!form.id && (!form.pin || !/^\d{4,6}$/.test(form.pin))) { toast.error('PIN 4-6 cifre obbligatorio'); return; }
    if (form.id && form.pin && !/^\d{4,6}$/.test(form.pin)) { toast.error('PIN deve essere 4-6 cifre'); return; }
    setSaving(true);
    try {
      if (form.id) {
        await adminApi.put(`/admin/riders/${form.id}`, {
          name: form.name, phone: form.phone, active: form.active, pin: form.pin || undefined,
        });
        toast.success('Rider aggiornato');
      } else {
        await adminApi.post('/admin/riders', { name: form.name, phone: form.phone, pin: form.pin });
        toast.success('Rider creato');
      }
      setForm(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Eliminare questo rider?')) return;
    try { await adminApi.delete(`/admin/riders/${id}`); toast.success('Rider eliminato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const toggleActive = async (r) => {
    try { await adminApi.put(`/admin/riders/${r.id}`, { active: !r.active }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const copyLoginLink = () => {
    const url = `${window.location.origin}/rider/login`;
    navigator.clipboard.writeText(url);
    toast.success('Link copiato! Condividilo con il rider');
  };

  if (loading) return <div className="p-8 flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="admin-riders-page">
      <PageTooltip page="riders" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2"><Bike size={22} className="text-[#D44E20]" /> Rider</h1>
          <p className="text-sm text-[#64748B] mt-1">Gestisci i fattorini interni. Il rider accede con cellulare + PIN.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={copyLoginLink} data-testid="copy-rider-link"
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-[#64748B] border border-slate-200 hover:bg-slate-50">
            🔗 Copia link login rider
          </button>
          <button onClick={startCreate} data-testid="create-rider-btn"
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A]">
            <Plus size={14}/> Nuovo rider
          </button>
        </div>
      </div>

      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 text-xs text-indigo-900">
        <p className="font-bold mb-1">💡 Come funziona</p>
        <p>1. Crea il rider con nome + cellulare + PIN (4-6 cifre). Comunica il PIN al rider in modo sicuro.</p>
        <p>2. Condividi il link <code className="bg-white px-1 rounded">{`${window.location.origin}/rider/login`}</code> — il rider lo salva nei bookmark.</p>
        <p>3. Dal pannello Ordini assegni le consegne a un rider. Il rider le vede nella sua dashboard in tempo reale.</p>
      </div>

      {form && (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm space-y-4" data-testid="rider-form">
          <h3 className="text-base font-semibold text-[#1A1A1A]">{form.id ? 'Modifica rider' : 'Nuovo rider'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5"><User size={11} className="inline mr-1"/> Nome</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="rider-name-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5"><Phone size={11} className="inline mr-1"/> Cellulare</label>
              <input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+39 333 1234567" data-testid="rider-phone-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5"><Key size={11} className="inline mr-1"/> PIN {form.id ? '(lascia vuoto per non cambiare)' : '(4-6 cifre)'}</label>
              <input value={form.pin} onChange={e => setForm(f => ({ ...f, pin: e.target.value }))} placeholder={form.id ? '●●●●●●' : '123456'} maxLength={6} data-testid="rider-pin-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="flex items-center gap-2 pt-6">
              <input type="checkbox" id="rider-active" checked={form.active} onChange={e => setForm(f => ({ ...f, active: e.target.checked }))} className="w-4 h-4" />
              <label htmlFor="rider-active" className="text-sm text-[#1A1A1A]">Attivo (può accedere e ricevere consegne)</label>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">🛵 Mezzo di consegna</label>
              <select value={form.vehicle} onChange={e => setForm(f => ({ ...f, vehicle: e.target.value }))} data-testid="rider-vehicle-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none">
                <option value="">— seleziona —</option>
                <option value="bicycle">🚲 Bici</option>
                <option value="ebike">⚡ E-bike</option>
                <option value="scooter">🛵 Scooter</option>
                <option value="moto">🏍️ Moto</option>
                <option value="car">🚗 Auto</option>
                <option value="walking">🚶 A piedi</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">⏰ Orari disponibilità</label>
              <input value={form.working_hours} onChange={e => setForm(f => ({ ...f, working_hours: e.target.value }))} placeholder="es. Mar-Ven 19-23 / Sab 12-15 e 19-24" data-testid="rider-hours-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold uppercase tracking-wider text-[#64748B] mb-1.5">📍 Zona di competenza</label>
              <input value={form.zone} onChange={e => setForm(f => ({ ...f, zone: e.target.value }))} placeholder="es. Centro, Trastevere, CAP 00186" data-testid="rider-zone-input"
                className="w-full h-10 bg-slate-50 border border-slate-200 rounded-xl px-3 text-sm focus:border-[#D44E20] focus:outline-none" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setForm(null)} className="px-4 py-2 rounded-xl text-sm text-[#64748B] hover:bg-slate-50">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="save-rider-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold text-white bg-[#D44E20] hover:bg-[#B3421A] disabled:opacity-60">
              <Save size={14}/> {saving ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </div>
      )}

      {riders.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200/60 p-12 text-center">
          <Bike size={40} className="mx-auto text-slate-300 mb-3" />
          <p className="text-[#1A1A1A] font-semibold">Nessun rider configurato</p>
          <p className="text-sm text-[#64748B] mt-1">Crea il primo rider per iniziare ad assegnare le consegne</p>
        </div>
      ) : (
        <div className="space-y-2">
          {riders.map(r => (
            <div key={r.id} data-testid={`rider-row-${r.id}`}
              className="bg-white rounded-2xl border border-slate-200/60 px-5 py-4 shadow-sm flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${r.active ? 'bg-emerald-500' : 'bg-slate-300'}`}>
                  {r.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="text-sm font-bold text-[#1A1A1A]">{r.name}</p>
                  <p className="text-xs text-[#64748B]">{r.phone}</p>
                  {(r.vehicle || r.working_hours || r.zone) && (
                    <p className="text-[10px] text-[#94a3b8] mt-0.5 flex flex-wrap gap-2">
                      {r.vehicle && <span>🛵 {r.vehicle}</span>}
                      {r.working_hours && <span>⏰ {r.working_hours}</span>}
                      {r.zone && <span>📍 {r.zone}</span>}
                    </p>
                  )}
                </div>
                {!r.active && <span className="text-[10px] font-bold bg-slate-100 text-slate-500 rounded-full px-2 py-0.5">INATTIVO</span>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => toggleActive(r)} data-testid={`toggle-rider-${r.id}`}
                  className="text-xs font-semibold text-[#64748B] hover:text-[#1A1A1A] border border-slate-200 px-3 py-1.5 rounded-lg hover:bg-slate-50">
                  {r.active ? 'Disattiva' : 'Attiva'}
                </button>
                <button onClick={() => startEdit(r)} data-testid={`edit-rider-${r.id}`}
                  className="w-8 h-8 rounded-lg text-[#64748B] hover:bg-slate-50 flex items-center justify-center">
                  <Edit2 size={13}/>
                </button>
                <button onClick={() => del(r.id)} data-testid={`delete-rider-${r.id}`}
                  className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 flex items-center justify-center">
                  <Trash2 size={13}/>
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
